import { configureStore } from '@reduxjs/toolkit';
import { apiSlice } from '@/services/api-slice';

/**
 * Importación de reducers de los diferentes módulos de la aplicación.
 * Cada reducer gestiona un dominio específico de la aplicación.
 */
import authReducer from '../features/auth/authSlice';
import accessControlReducer from '../features/access-control/access-controlSlice';
import userReducer from '../features/users/usersSlice';
import usersGroupsReducer from '../features/users-groups/users-groupsSlice';
import companyReducer from '../features/company/companySlice';
import productReducer from '../features/materials/materialsSlice';
import locationReducer from '../features/ubications/ubicationsSlice';
import catalogueReducer from '../features/materials/categories/categoriesSlice';
import movementReducer from '../features/materials/movements/movementsSlice';
import voucherReducer from '../features/materials/vouchers/vouchersSlice';
import supplierReducer from '../features/suppliers/suppliersSlice';
import workordersReducer from '../features/works-orders/works-ordersSlice';
import workspacesReducer from '../features/workspaces/workspacesSlice';
import rolesWorkspacesReducer from '../features/workspaces/roles-workspaces/roles-workspacesSlice';
import externalOtReducer from '../features/works-orders/external-ot/external-otSlice';
import externalOtsReducer from '../features/works-orders/external-ots/external-otsSlice';
import systemSettingsReducer from '../features/systems-settings/systems-settingsSlice';
import certificationsReducer from '../features/works-orders/certifications/certificationsSlice';
import certificationRecordsReducer from '../features/finance/certification-records/certification-recordsSlice';


export const store = configureStore({
  reducer: {
    auth: authReducer, // Gestión de autenticación
    users: userReducer,
    accessControl: accessControlReducer,
    usersGroups: usersGroupsReducer,
    company: companyReducer,
    products: productReducer,
    ubications: locationReducer,
    catalogue: catalogueReducer,
    movements: movementReducer,
    vouchers: voucherReducer,
    suppliers: supplierReducer,
    workorders: workordersReducer,
    workspaces: workspacesReducer,
    rolesWorkspaces: rolesWorkspacesReducer,
    externalOt: externalOtReducer,
    externalOts: externalOtsReducer,
    certifications: certificationsReducer,
    certificationRecords: certificationRecordsReducer,
    systemSettings: systemSettingsReducer,
    // users: userReducer, // Gestión de usuarios y grupos de usuarios
    [apiSlice.reducerPath]: apiSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Configuración adicional si necesitas
      serializableCheck: {
        // Ignorar acciones de async thunk y otras no serializables
        ignoredActions: ['persist/PERSIST'], // Si usas redux-persist
      },
    }).concat(
      apiSlice.middleware,
      // Aquí se agregan automáticamente los thunks con getDefaultMiddleware()
    ),
  /**
   * Habilitación condicional de herramientas de desarrollo según entorno.
   * @security ISO27001: A.12.5.1 - Instalación de software en sistemas operativos
   */
  devTools: import.meta.env.MODE !== 'production',
});


/**
 * Exportación de tipos para facilitar el acceso tipado al estado global
 * y a las funciones de dispatch.
 * @security ISO27001: A.14.2.5 - Principios de ingeniería para sistemas seguros
 */

/**
 * Tipo que representa el estado global de la aplicación.
 * @typedef {Object} RootState
 */
export type RootState = ReturnType<typeof store.getState>;

/**
 * Tipo que representa el dispatcher de la aplicación.
 * @typedef {Function} AppDispatch
 */
export type AppDispatch = typeof store.dispatch;

